import styles from "./ResetPassword.module.scss";

import { ChangeEvent, FormEvent, useMemo, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";

import { AppButton } from "@/modules/shared/components/AppButton";
import { AppInput } from "@/modules/shared/components/AppInput";
import { PageContainer } from "@/modules/shared/components/PageContainer";
import { ApiError } from "@/modules/shared/utils/ApiError";
import { captureApiError } from "@/modules/shared/utils/captureApiError";
import { Route } from "@/routes/reset-password";

import { createResetPasswordFormSchema } from "../../schemas/resetPasswordFormSchema";
import { confirmPasswordReset } from "../../services/confirmPasswordReset";

const INVALID_LINK_STATUS = 417;

type FormErrors = {
  password?: string;
  confirmPassword?: string;
};

export function ResetPasswordScreen() {
  const { t } = useTranslation();
  const { user_id: userId, token } = Route.useSearch();
  const confirmPasswordResetMutation = useMutation({
    mutationFn: confirmPasswordReset,
  });

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [hasInvalidLink, setHasInvalidLink] = useState(false);

  const schema = useMemo(() => createResetPasswordFormSchema(t), [t]);

  const handlePasswordChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPassword(e.target.value);
    if (errors.password) {
      setErrors((prev) => ({ ...prev, password: undefined }));
    }
    if (submissionError) {
      setSubmissionError(null);
    }
  };

  const handleConfirmPasswordChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setConfirmPassword(e.target.value);
    if (errors.confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
    }
    if (submissionError) {
      setSubmissionError(null);
    }
  };

  const validateForm = (): boolean => {
    const result = schema.safeParse({ password, confirmPassword });

    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors;
      setErrors({
        password: fieldErrors.password?.[0],
        confirmPassword: fieldErrors.confirmPassword?.[0],
      });
      return false;
    }

    setErrors({});
    return true;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmissionError(null);

    confirmPasswordResetMutation.mutate(
      { userId, token, password },
      {
        onSuccess: () => {
          setIsSuccess(true);
        },
        onError: (error) => {
          if (error instanceof ApiError) {
            captureApiError(error);

            if (error.status === INVALID_LINK_STATUS) {
              setHasInvalidLink(true);
              return;
            }

            if (error.errorClass === "4xx") {
              setSubmissionError(t("resetPassword.errors.genericError"));
              return;
            }
          }

          setSubmissionError(t("resetPassword.errors.genericError"));
        },
      },
    );
  };

  if (isSuccess) {
    return (
      <PageContainer variant="centered">
        <div className={styles.wrapper}>
          <div className={styles.container}>
            <div className={styles.copySection}>
              <h1 className={styles.title}>
                {t("resetPassword.successTitle")}
              </h1>
            </div>
            <div className={styles.successContent}>
              <p>{t("resetPassword.successDescription")}</p>
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  if (hasInvalidLink) {
    return (
      <PageContainer variant="centered">
        <div className={styles.wrapper}>
          <div className={styles.container}>
            <div className={styles.copySection}>
              <h1 className={styles.title}>{t("resetPassword.title")}</h1>
              <p className={styles.formError} role="alert">
                {t("resetPassword.errors.invalidLink")}
              </p>
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  const isPending = confirmPasswordResetMutation.isPending;
  const isSubmitDisabled = isPending || !password || !confirmPassword;

  return (
    <PageContainer variant="centered">
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <div className={styles.copySection}>
            <h1 className={styles.title}>{t("resetPassword.title")}</h1>
            <p className={styles.subtitle}>
              <Trans
                i18nKey="resetPassword.subtitle"
                components={{
                  emphasis: <span className={styles.subtitleEmphasis} />,
                }}
              />
            </p>
          </div>

          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <AppInput
              id="new-password"
              type="password"
              size="large"
              label={t("resetPassword.newPasswordLabel")}
              placeholder={t("resetPassword.newPasswordPlaceholder")}
              value={password}
              onChange={handlePasswordChange}
              error={errors.password}
              disabled={isPending}
              autoComplete="new-password"
              showPasswordToggle
              passwordToggleLabel={{
                show: t("resetPassword.showPassword"),
                hide: t("resetPassword.hidePassword"),
              }}
            />

            <AppInput
              id="confirm-new-password"
              type="password"
              size="large"
              label={t("resetPassword.confirmPasswordLabel")}
              placeholder={t("resetPassword.confirmPasswordPlaceholder")}
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              error={errors.confirmPassword}
              disabled={isPending}
              autoComplete="new-password"
              showPasswordToggle
              passwordToggleLabel={{
                show: t("resetPassword.showPassword"),
                hide: t("resetPassword.hidePassword"),
              }}
            />

            {submissionError && (
              <p className={styles.formError} role="alert">
                {submissionError}
              </p>
            )}

            <div className={styles.actions}>
              <AppButton
                type="submit"
                variant="primary"
                disabled={isSubmitDisabled}
                isLoading={isPending}
                className={styles.saveButton}
              >
                {t("resetPassword.submitButton")}
              </AppButton>
            </div>
          </form>
        </div>
      </div>
    </PageContainer>
  );
}
